import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Iconify from 'src/components/iconify';
import { fData } from 'src/utils/format-number';
import axiosInstance from 'src/api/axiosInstance';
import { getCurrentProject, getCurrentServer, getMandEndPoint } from 'src/helper/server-address';
import { fileData } from '../file-thumbnail';

export default function RejectionFiles({
  fileRejections = [],
  maxSize = 3 * 1024 * 1024,
  acceptFile,
}) {
  const [showErrorBox, setShowErrorBox] = useState(false);
  const token = sessionStorage.getItem('token');

  // Always show box if new rejections appear
  useEffect(() => {
    if (fileRejections.length > 0) {
      setShowErrorBox(true);
    }
  }, [fileRejections]);  

  // Don't show box at all if no files or box is closed
  if (!fileRejections.length || !showErrorBox) return null;

  const getFileExtensions = () => {
    const extensions = new Set();

    Object.keys(acceptFile || {}).forEach((type) => {
      switch (type) {
        case 'application/pdf':
          extensions.add('pdf');
          break;
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          extensions.add('doc/docx');
          break;
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          extensions.add('xls/xlsx');
          break;
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        case 'application/vnd.ms-powerpoint':
          extensions.add('ppt/pptx');
          break;
        case 'text/plain':
          extensions.add('txt');
          break;
        case 'image/*':
          extensions.add('png');
          extensions.add('jpg');
          extensions.add('jpeg');
          extensions.add('gif');
          extensions.add('bmp');
          extensions.add('webp');
          extensions.add('tiff');
          extensions.add('heic');
          extensions.add('avif');
          extensions.add('ico');
          extensions.add('jfif');
          break;
        default:
          break;
      }
    });

    return Array.from(extensions).join(', ');
  };

  const truncateFileName = (fileName, maxLength = 40) => {
    if (fileName.length <= maxLength) return fileName;
    const start = fileName.substring(0, maxLength / 2);
    const end = fileName.substring(fileName.length - maxLength / 2);
    return `${start}...${end}`;
  };

  const formatSize = (size) => {
    if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${size} bytes`;
  };

  const getErrorMessage = (error) => {
    switch (error.code) {
      case 'file-too-large':
        return `- File size must be smaller than ${formatSize(maxSize)}.`;
      case 'file-too-small':
        return `- File size must be greater than 5 KB.`;
      case 'file-invalid-type':
        return `- Invalid file type. File type must be ${getFileExtensions()}.`;
      default:
        return `- ${error.message}`;
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
        borderColor: (theme) => alpha(theme.palette.error.main, 0.24),
        position: 'relative',
      }}
    >
      {/* Close (X) Button */}
      <IconButton
        size="small"
        onClick={() => setShowErrorBox(false)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'error.main',
        }}
      >
        <Iconify icon="mingcute:close-line" />
      </IconButton>

      {/* Error List */}
      {fileRejections.map(({ file, errors }) => {
        const { path, size } = fileData(file);
        return (
          <Box key={path} sx={{ my: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {truncateFileName(path)} - {size ? fData(size) : ''}
            </Typography>

            {errors.map((error) => (
              <Box
                key={error.code}
                component="span"
                sx={{
                  typography: 'caption',
                  color: 'error.main',
                  borderRadius: 1,
                  padding: 0.5,
                  display: 'inline-block',
                  marginTop: 0.5,
                  fontWeight: 'bold',
                }}
              >
                {getErrorMessage(error)}
              </Box>
            ))}
          </Box>
        );
      })}
    </Paper>
  );
}

RejectionFiles.propTypes = {
  fileRejections: PropTypes.array,
  maxSize: PropTypes.number,
  acceptFile: PropTypes.object,
};
